import { Link } from 'react-router-dom';
import { AlertTriangle, Clock } from 'lucide-react';
import { useBilling } from '@/api/billing';
import { useAuth } from '@/app/auth/useAuth';
import { cn } from '@/lib/cn';

/**
 * Trial ending, payment overdue, or paused workspace — shown to whoever can see billing (owner, admins),
 * linking to Settings → Billing. Nothing for healthy or operator-managed workspaces.
 */
export function BillingBanner() {
  const { workspace, can, isPlatformAdmin } = useAuth();
  const enabled = !!workspace && !isPlatformAdmin && can('read', 'Billing');
  const billing = useBilling(enabled);
  const b = billing.data;
  if (!enabled || !b) return null;

  let text: string | null = null;
  let urgent = false;
  if (b.state === 'trialing' && b.daysLeft !== null && b.daysLeft <= 5) {
    text =
      b.daysLeft === 0
        ? 'Your free trial ends today.'
        : `Your free trial ends in ${b.daysLeft} day${b.daysLeft === 1 ? '' : 's'}.`;
  } else if (b.state === 'past_due') {
    text = 'Your last payment didn’t go through — update your card to keep the AI agent running.';
    urgent = true;
  } else if (b.state === 'trial_ended') {
    text = 'Your free trial has ended. The AI agent is paused until you choose a plan.';
    urgent = true;
  } else if (b.state === 'expired') {
    text = 'Your subscription has expired. The AI agent is paused until you renew.';
    urgent = true;
  }
  if (!text) return null;

  const Icon = urgent ? AlertTriangle : Clock;
  return (
    <div
      role="status"
      className={cn(
        'flex flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 py-2 text-center text-sm',
        urgent ? 'bg-red-600 text-white' : 'bg-brand-50 text-brand-900',
      )}
    >
      <span className="inline-flex items-center gap-1.5">
        <Icon className="h-4 w-4 shrink-0" /> {text}
      </span>
      <Link
        to="/settings/billing"
        className={cn('font-semibold underline underline-offset-2', urgent ? 'text-white' : 'text-brand-700')}
      >
        {b.state === 'past_due' ? 'Update payment' : 'See plans'}
      </Link>
    </div>
  );
}
